"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck, Hammer, Zap, Calendar, Clock, ArrowRight, ShieldCheck, Coins, Compass, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface CostDriver {
  id: string;
  title: string;
  timeframe: string;
  impact: string;
  description: string;
  iconName: "FileCheck" | "Hammer" | "Zap";
  details: string;
}
interface TimelinePhase {
  id: string;
  phase: string;
  title: string;
  duration: string;
  summary: string;
}
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const costDriversData: CostDriver[] = [{
  id: "driver-permits",
  title: "Permitting & City of Brookhaven Approvals",
  timeframe: "2–4 Weeks",
  impact: "Standard Fixed Process",
  description: "Municipal zoning reviews, architectural plan approvals, and neighborhood HOA coordination across Brookhaven, Murphey Candler, Brittany, and Historic Brookhaven.",
  iconName: "FileCheck",
  details: "Includes certified structural engineering reviews, DeKalb County water coordination, and jurisdictional health and safety clearances."
}, {
  id: "driver-cabinetry",
  title: "Bespoke Cabinetry & Architectural Millwork",
  timeframe: "8–12 Weeks Lead Time",
  impact: "Primary Value Driver",
  description: "Handcrafted furniture-grade boxes, inset mortise-and-tenon doors, integrated appliance panels, custom pantries, and specialized hood surrounds.",
  iconName: "Hammer",
  details: "Fabricated to millimeter specifications during early planning so millwork arrives concurrently with rough mechanical sign-offs."
}, {
  id: "driver-structural",
  title: "Structural Reconfiguration & MEP Upgrades",
  timeframe: "3–5 Weeks Active",
  impact: "Variable by Home Age",
  description: "Load-bearing wall removals, engineered LVL header installations, subfloor leveling, subpanel expansions, and modern plumbing reroutes.",
  iconName: "Zap",
  details: "Frequently required in 1950s–1980s Brookhaven ranch and split-level layouts transitioning toward open-concept culinary living."
}];
const timelinePhasesData: TimelinePhase[] = [{
  id: "phase-1",
  phase: "Phase 1",
  title: "Architectural Design & Selections",
  duration: "Weeks 1–4",
  summary: "Comprehensive laser spatial measurement, 3D photorealistic renderings, finish specifications, fixture schedules, and fixed-price scope confirmation."
}, {
  id: "phase-2",
  phase: "Phase 2",
  title: "Brookhaven Permitting & Millwork Procurement",
  duration: "Weeks 5–8",
  summary: "Municipal submission to Brookhaven Community Development, HOA documentation, cabinet shop drawing sign-offs, and procurement of long-lead appliances."
}, {
  id: "phase-3",
  phase: "Phase 3",
  title: "Demolition & Rough Mechanicals",
  duration: "Weeks 9–11",
  summary: "Negative-pressure sealed dust barriers, careful selective tear-out, framing reconfigurations, plumbing rough-in, electrical rewiring, and municipal inspection."
}, {
  id: "phase-4",
  phase: "Phase 4",
  title: "Cabinetry, Stone Fabrication & Finish Trim",
  duration: "Weeks 12–15",
  summary: "Bespoke millwork installation, digital laser countertop templating, honed quartzite or marble setting, hand-laid tile backsplashes, and architectural trim."
}, {
  id: "phase-5",
  phase: "Phase 5",
  title: "Final Punchout & Handover",
  duration: "Week 16",
  summary: "Mechanical calibration, deep post-construction detailing, City of Brookhaven final building inspections, and an exhaustive homeowner walkthrough."
}];
const faqData: FaqItem[] = [{
  id: "faq-contingency",
  question: "What contingency planning should Brookhaven homeowners prepare for?",
  answer: "We recommend setting aside a 10% to 15% qualitative contingency reserve, particularly for homes built prior to 1990. While comprehensive discovery and digital scans eliminate most unknowns, opening historical subfloors and wall cavities in established Brookhaven neighborhoods can occasionally reveal prior unpermitted wiring or legacy galvanized pipes that benefit from immediate renewal."
}, {
  id: "faq-living",
  question: "Can our family comfortably live in the home during construction?",
  answer: "Yes. Most clients remain in their homes throughout construction. We build negative-pressure zippered HEPA containment walls, isolate ductwork to protect living areas, establish dedicated temporary food preparation stations with refrigeration, and enforce respectful work hours strictly during weekdays."
}, {
  id: "faq-change-orders",
  question: "How are mid-project scope adjustments or design refinements managed?",
  answer: "Every adjustment is documented with explicit line-item material pricing and exact schedule impact before any construction proceeds. No unexpected charges or unapproved work orders ever appear on your account."
}];
export default function Timeline() {
  const shouldReduceMotion = useReducedMotion();
  const motionInitial = shouldReduceMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 15
  };
  const motionAnimate = {
    opacity: 1,
    y: 0
  };
  const motionTransition = {
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1] as const
  };
  return <section id="cost-timeline" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
        once: true
      }} transition={motionTransition} className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-widest text-foreground uppercase">
              <Compass className="size-3.5 text-primary" />
              Investment & Planning
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Understanding your kitchen remodel cost and timeline in Brookhaven
          </h2>

          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A conversion-focused renovation demands complete predictability. Here is our
            transparent breakdown of jurisdictional permits, bespoke millwork schedules, and
            structural scope realities across Brookhaven residences.
          </p>
          <div className="mt-6 mx-auto h-[1px] w-24 bg-primary/40" />
        </motion.div>

        {/* Asymmetric Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: Cost Drivers (5 Columns on desktop) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center justify-between pb-2 border-b border-border/80">
              <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                Core Cost Drivers
              </h3>
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Scope Architecture
              </span>
            </div>

            {costDriversData.map((driver, index) => <motion.div key={driver.id} data-index={index} initial={motionInitial} whileInView={motionAnimate} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: shouldReduceMotion ? 0 : index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }}>
                <Card className="rounded-2xl border border-border/80 bg-card shadow-sm transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary border border-primary/20">
                        {driver.iconName === "FileCheck" && <FileCheck className="size-5" />}
                        {driver.iconName === "Hammer" && <Hammer className="size-5" />}
                        {driver.iconName === "Zap" && <Zap className="size-5" />}
                      </div>
                      <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/5 text-foreground text-xs font-semibold px-2.5 py-0.5">
                        {driver.impact}
                      </Badge>
                    </div>

                    <CardTitle className="text-lg font-semibold text-foreground pt-3 font-default">
                      {driver.title}
                    </CardTitle>
                    <div className="flex items-center gap-1.5 text-xs text-primary font-medium mt-1">
                      <Clock className="size-3.5" />
                      <span>{driver.timeframe}</span>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 text-sm text-muted-foreground space-y-3">
                    <p className="leading-relaxed">{driver.description}</p>
                    <div className="rounded-lg bg-muted/60 p-3 border border-border/60 text-xs text-foreground/80 leading-normal flex items-start gap-2">
                      <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                      <span>{driver.details}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* Local Assistance Callout */}
            <div className="rounded-xl border border-border bg-muted/40 p-5 mt-2 flex items-start gap-3">
              <ShieldCheck className="size-5 shrink-0 text-primary mt-0.5" />
              <div className="text-xs text-muted-foreground leading-relaxed">
                <strong className="font-semibold text-foreground">
                  Brookhaven Permitting Assistance:
                </strong>{" "}
                We serve as your dedicated representative with the City of Brookhaven Community
                Development Department, managing plan submissions, zoning compliance, trade permits,
                and scheduled building inspections from initial intake through final inspection.
              </div>
            </div>
          </div>

          {/* Right Column: Phased Timeline & Accordion (7 Columns on desktop) */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            {/* Phased Timeline Card */}
            <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
            once: true
          }} transition={motionTransition}>
              <Card className="rounded-2xl border border-border/80 bg-card shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between pb-6 border-b border-border/80">
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Project Road Map
                    </span>
                    <h3 className="font-serif text-2xl font-semibold text-foreground mt-1">
                      Step-by-Step Construction Schedule
                    </h3>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted px-3 py-1 rounded-full border border-border">
                    <SafeHydrate><Calendar className="size-3.5 text-primary" /></SafeHydrate>
                    <span>Average 16-Week Duration</span>
                  </div>
                </div>

                {/* Timeline Step Items */}
                <div className="mt-8 space-y-6 relative before:absolute before:left-3.5 before:top-2 before:bottom-3 before:w-[2px] before:bg-border/80">
                  {timelinePhasesData.map((item, index) => <div key={item.id} data-index={index} className="relative flex items-start gap-4 pl-1">
                      {/* Timeline Node */}
                      <div className="size-7 rounded-full bg-card border-2 border-primary text-primary flex items-center justify-center shrink-0 z-10 shadow-xs">
                        <span className="text-xs font-bold font-mono">{index + 1}</span>
                      </div>

                      {/* Content Box */}
                      <div className="flex-1 rounded-xl bg-muted/30 p-4 border border-border/60 transition-[border-color,background-color] duration-200 hover:border-primary/40 hover:bg-muted/50">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                          <span className="text-xs font-bold uppercase tracking-wider text-primary">
                            {item.phase}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground/80 bg-background px-2.5 py-0.5 rounded-md border border-border">
                            <Clock className="size-3 text-muted-foreground" />
                            {item.duration}
                          </span>
                        </div>
                        <h4 className="text-base font-semibold text-foreground">
                          {item.title}
                        </h4>
                        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                          {item.summary}
                        </p>
                      </div>
                    </div>)}
                </div>
              </Card>
            </motion.div>

            {/* Accordion Component for Homeowner Concerns */}
            <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
            once: true
          }} transition={{
            ...motionTransition,
            delay: 0.1
          }}>
              <Card className="rounded-2xl border border-border/80 bg-card shadow-sm p-6 sm:p-8">
                <div className="mb-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Transparency & Standards
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground mt-1">
                    Frequently Addressed Planning Questions
                  </h3>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {faqData.map((faq, index) => <div key={faq.id} data-index={index}>
                      <AccordionItem value={faq.id} className="border-border">
                        <AccordionTrigger className="text-left font-default font-semibold text-foreground text-sm sm:text-base hover:no-underline hover:text-primary py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground leading-relaxed pr-4">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    </div>)}
                </Accordion>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Consultation Call to Action Card */}
        <motion.div initial={motionInitial} whileInView={motionAnimate} viewport={{
        once: true
      }} transition={{
        ...motionTransition,
        delay: 0.15
      }} className="mt-12 sm:mt-16">
          <div className="relative rounded-2xl border border-primary/30 bg-secondary text-secondary-foreground p-8 sm:p-12 shadow-md overflow-hidden">
            <div className="absolute right-0 top-0 -mt-10 -mr-10 size-60 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary">
                  <Coins className="size-3.5" />
                  No-Obligation Assessment
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-secondary-foreground mt-2 leading-tight">
                  Request a preliminary kitchen scope & investment review
                </h3>
                <p className="mt-2 text-sm sm:text-base text-secondary-foreground/80 leading-relaxed">
                  Share your floor plan or vision and we will follow up to discuss your project, evaluating structural feasibility, permit timelines, and providing transparent cost estimates.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full md:w-auto">
                <Button asChild size="lg" className="rounded-lg bg-primary px-7 py-3.5 font-semibold text-primary-foreground shadow-sm transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-primary/90">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                    Request an Estimate
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-lg border-border/40 bg-secondary/80 text-secondary-foreground hover:bg-secondary/60 transition-colors">
                  <Link to="/OurProjectsPage">Explore Kitchen Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}